import { useEffect } from 'react';
import SSEConsumer from '@shared/services/sse-consumer';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import {
  NotificationCategories,
  NotificationType,
  useNotificationsActions,
} from '@src/shared/context/notifications';
import { DrawerType, useInsideDrawerState } from '@src/shared/context/inside-drawer';
import { useBoundDashboardStore } from '@src/pages/DeliveryDashboard/store/dashboardGlobalStore';
import { DashboardServerEventType, OrderStatus } from '../models/model-enum';
import {
  ICourierETA,
  IOrderETA,
  ICourierLocation,
  ICourierReturnStatus,
  ICourierSnap,
  IEta,
  IOrder,
  IRoute,
  IServerTime,
} from '../models/model-interface';
import { useDashboardUIStore } from '../store/dashboardUIStore';
import { useTranslation } from 'react-i18next';
import useDashboardStore from './use-dashboard-store';
import MCPMarketEnvironmentDetection, {
  MCP_MARKET_ENVIRONMENT_COUNTRY_CODES,
} from '@src/shared/utils/market-environment-detection';

const useDashboardEventSource = (SSEerror = false) => {
  const { notify } = useNotificationsActions(NotificationCategories.Dashboard);
  const { activeDrawerState } = useInsideDrawerState();
  const { restaurant, keycloakToken } = useKeycloakUserState();
  const { setIsDataLoaded, setNewOrderReceived } = useDashboardUIStore();
  const {
    setCouriersList,
    setOrdersList,
    addOrder,
    setRoutesList,
    setCourierStatus,
    updateCourier,
    updateCourierStatus,
    updateOrder,
    updateRoute,
    setCourierLocation,
    setCourierEta,
    updateCourierEta,
    setNextRouteList,
    updateNextRoute,
    getOrderByHash,
    setServerTime,
    setOrderEta,
    updateOrderEta,
  } = useBoundDashboardStore();

  const { t } = useTranslation();

  const getSSEBaseUrl = () => {
    const { countryCode } = MCPMarketEnvironmentDetection.getMarketEnvironment();
    if (countryCode === MCP_MARKET_ENVIRONMENT_COUNTRY_CODES.UK) {
      return process.env.DELIVERIES_SSE_SOURCE_URL_EU_WEST as string;
    }
    return process.env.DELIVERIES_SSE_SOURCE_URL_EU_CENTRAL as string;
  };

  const getSSEInstance = (usersId?: string, token?: string) => {
    if (!usersId || !token) return null;
    return SSEConsumer.getInstance(getSSEBaseUrl(), token, usersId);
  };

  const notifyOrderUpdate = (order: IOrder) => {
    if (
      order.status === OrderStatus.AssignedToNextPool ||
      order.status === OrderStatus.InDelivery ||
      order.status === OrderStatus.Delivered
    ) {
      return;
    }
    const orderData = getOrderByHash(order.hash);

    if (!orderData) {
      return;
    }
    const courierName = `${orderData.courier?.firstName || ''} ${
      orderData.courier?.lastName || ''
    }`;
    const selectedNotificationType = {
      [OrderStatus.Assigned]: NotificationType.Success,
      [OrderStatus.Unassigned]: NotificationType.Warning,
    };
    const selectedNotificationTitle = {
      [OrderStatus.Assigned]: t('notification_headers.orderAssignSuccessHeader'),
      [OrderStatus.Unassigned]: t('notification_headers.orderUnassignSuccessHeader'),
    };

    const selectedNotificationMessage = {
      [OrderStatus.Assigned]: t(`graphql.SUCCESS_ORDER_ASSIGN`, {
        courierName: courierName,
        orderReferance: `${orderData.customer.address.street} ${orderData.customer.address.streetNumber}`,
      }),
      [OrderStatus.Unassigned]: t(`graphql.SUCCESS_ORDER_UNASSIGN`, {
        courierName,
        orderId: `${orderData.customer.address.street} ${orderData.customer.address.streetNumber}`,
      }),
    };

    notify({
      message: selectedNotificationMessage[order.status],
      title: selectedNotificationTitle[order.status],
      type: selectedNotificationType[order.status],
      showOnlyInDrawer: true,
      seen: activeDrawerState === DrawerType.NOTIFICATION,
    });
  };

  useEffect(() => {
    if (!keycloakToken || !restaurant?.id) {
      return;
    }

    SSEConsumer.destroyInstance();
    const consumer = getSSEInstance(restaurant.id, keycloakToken);

    if (!consumer) return;

    consumer.subscribeToEvent(
      DashboardServerEventType.CourierListSnapshot,
      (payload: ICourierSnap[]) => {
        setIsDataLoaded(!!(payload.length > 0));
        setCouriersList(payload);
      }
    );

    consumer.subscribeToEvent(DashboardServerEventType.CourierUpdated, (payload: ICourierSnap) => {
      updateCourier(payload);
    });

    consumer.subscribeToEvent(
      DashboardServerEventType.OrderEtaListSnapshot,
      (payload: IOrderETA[]) => {
        setOrderEta(payload);
      }
    );

    consumer.subscribeToEvent(DashboardServerEventType.OrderEtaUpdated, (payload: IOrderETA) => {
      updateOrderEta(payload);
    });

    consumer.subscribeToEvent(DashboardServerEventType.OrderListSnapshot, (payload: IOrder[]) => {
      setOrdersList(payload);
    });

    consumer.subscribeToEvent(DashboardServerEventType.OrderUpdated, (payload: IOrder) => {
      //notifyOrderUpdate(payload);
      updateOrder(payload);
    });

    consumer.subscribeToEvent(DashboardServerEventType.OrderCreated, (payload: IOrder) => {
      addOrder(payload);
      if (payload.status === OrderStatus.Unassigned) {
        setNewOrderReceived();
      }
    });

    consumer.subscribeToEvent(DashboardServerEventType.RouteListSnapshot, (payload: IRoute[]) => {
      setRoutesList(payload);
    });

    consumer.subscribeToEvent(DashboardServerEventType.RouteUpdated, (payload: IRoute) => {
      updateRoute(payload);
    });

    consumer.subscribeToEvent(
      DashboardServerEventType.CourierStatusListSnapshot,
      (payload: ICourierReturnStatus[]) => {
        setCourierStatus(payload);
      }
    );

    consumer.subscribeToEvent(
      DashboardServerEventType.CourierStatusUpdated,
      (payload: ICourierReturnStatus) => {
        updateCourierStatus(payload);
      }
    );

    consumer.subscribeToEvent(
      DashboardServerEventType.CourierLocationUpdated,
      (payload: ICourierLocation) => {
        setCourierLocation(payload);
      }
    );

    consumer.subscribeToEvent(
      DashboardServerEventType.EtaListSnapshot,
      (payload: ICourierETA[]) => {
        setCourierEta(payload);
      }
    );

    consumer.subscribeToEvent(DashboardServerEventType.EtaUpdated, (payload: ICourierETA) => {
      updateCourierEta(payload);
    });

    consumer.subscribeToEvent(
      DashboardServerEventType.NextRouteListSnapshot,
      (payload: IRoute[]) => {
        setNextRouteList(payload);
      }
    );

    consumer.subscribeToEvent(DashboardServerEventType.NextRouteUpdated, (payload: IRoute) => {
      updateNextRoute(payload);
    });

    consumer.subscribeToEvent(DashboardServerEventType.ServerTime, (payload: IServerTime) => {
      setServerTime(payload.heartbeat);
      // Handle server time heartbeat if needed
    });

    /*    consumer.subscribeToEvent(
         DeliveriesServerEventType.OrderListSnapshot,
         (data: IOrder[]) => {
           data.map((order) => snapshotActiveOrders.push(order.uuid));
           setOrders(Object.assign(data, { expireTime: getNowStamp }));
   
         }
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.OrderCreated,
         (data: IOrder) => {
           addOrder(data);
           if (data.courier) {
             addCourier(data.courier);
           }
         }
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.OrderUpdated,
         (data: IOrder) => {
           updateOrder(data);
           notifyOrderUpdate(data)
           if (data.courier) {
             addCourier(data.courier);
           }
   
         }
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.OrderExpired,
         expiredOrder
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.CourierListSnapshot,
         (data: ICourier) => {
           //setCouriers(data);
           setCouriersList(data)
         }
       );
   
       consumer.subscribeToEvent(DeliveriesServerEventType.CourierUpdated, updateCourier);
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.CourierLocationUpdated,
         updateCourierLocation
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.CourierStatusListSnapshot,
         setCourierStatus
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.CourierStatusUpdated,
         (status: ICourierReturnStatus) => {
           updateCourierStatus(status)
           notifyCourierUpdate(status)
         }
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.RouteListSnapshot,
         (data) => {
           setCourierRoutes(data, snapshotActiveOrders);
         }
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.RouteUpdated,
         updateCourierRoute
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.EtaListSnapshot,
         setEtaLists
       );
   
       consumer.subscribeToEvent(
         DeliveriesServerEventType.CourierDeleted,
         deleteCourier
       );
   
       consumer.subscribeToEvent(DeliveriesServerEventType.EtaUpdated, updateEta);
   
       consumer.subscribeToEvent(DeliveriesServerEventType.NextRouteUpdated, updateNextRoute);
   
       consumer.subscribeToEvent(DeliveriesServerEventType.NextRouteListSnapshot, (data) => {
         if (data.length) {
           setNextRoute(data[0])
         }
       }) */

    return () => {
      SSEConsumer.destroyInstance();
    };
  }, [activeDrawerState === DrawerType.NOTIFICATION, keycloakToken, restaurant?.id]);
};

export default useDashboardEventSource;
